import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CompareField {
  key: string;
  label: string;
  oldValue: string;
  newValue: string;
}

interface CompareTagsField {
  currentTags: string[];
  newTags: string[];
}

interface MetadataCompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  fields?: CompareField[];
  tagsField?: CompareTagsField;
  onConfirm: (selections: Record<string, 'old' | 'new'>, selectedNewTags: string[]) => void;
  loading?: boolean;
}

export function MetadataCompareDialog({
  open,
  onOpenChange,
  title,
  fields = [],
  tagsField,
  onConfirm,
  loading = false,
}: MetadataCompareDialogProps) {
  // Initialize field selections - default to 'new' if values are different
  const [selections, setSelections] = useState<Record<string, 'old' | 'new'>>(() => {
    const initial: Record<string, 'old' | 'new'> = {};
    fields.forEach((field) => {
      initial[field.key] = field.oldValue !== field.newValue ? 'new' : 'old';
    });
    return initial;
  });

  // Initialize tag selections - all new tags selected by default
  const [selectedNewTags, setSelectedNewTags] = useState<string[]>(
    tagsField?.newTags || []
  );

  const handleFieldSelect = (key: string, value: 'old' | 'new') => {
    setSelections((prev) => ({ ...prev, [key]: value }));
  };

  const handleTagToggle = (tag: string) => {
    setSelectedNewTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSelectAllTags = () => {
    if (tagsField) {
      setSelectedNewTags(tagsField.newTags);
    }
  };

  const handleDeselectAllTags = () => {
    setSelectedNewTags([]);
  };

  const handleConfirm = () => {
    onConfirm(selections, selectedNewTags);
  };

  const changedFields = fields.filter((f) => f.oldValue !== f.newValue);
  const hasChanges = changedFields.length > 0 || (tagsField && tagsField.newTags.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] h-[85vh] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto py-4">
          {!hasChanges && (
            <p className="text-base text-muted-foreground text-center py-12">
              변경된 내용이 없습니다.
            </p>
          )}

          {hasChanges && (
            <div className="space-y-6">
              {/* Column Headers */}
              <div className="grid grid-cols-2 gap-4 px-2">
                <div className="text-sm font-semibold text-muted-foreground text-center flex items-center justify-center gap-2 py-2 bg-muted/50 rounded-lg">
                  <ArrowLeft className="w-4 h-4" />
                  현재 정보
                </div>
                <div className="text-sm font-semibold text-primary text-center flex items-center justify-center gap-2 py-2 bg-primary/10 rounded-lg">
                  새로 가져온 정보
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>

              {/* Field comparisons - Side by Side */}
              {changedFields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <div className="text-sm font-medium text-foreground px-2">
                    {field.label}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Old Value - Left */}
                    <button
                      type="button"
                      onClick={() => handleFieldSelect(field.key, 'old')}
                      className={cn(
                        'relative p-4 rounded-lg border-2 text-left transition-all min-h-[80px]',
                        'hover:border-muted-foreground/50',
                        selections[field.key] === 'old'
                          ? 'border-primary bg-primary/5'
                          : 'border-muted bg-muted/30'
                      )}
                    >
                      {selections[field.key] === 'old' && (
                        <div className="absolute top-3 right-3">
                          <Check className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <p className="text-sm leading-relaxed pr-6">
                        {field.oldValue || <span className="text-muted-foreground italic">없음</span>}
                      </p>
                    </button>

                    {/* New Value - Right */}
                    <button
                      type="button"
                      onClick={() => handleFieldSelect(field.key, 'new')}
                      className={cn(
                        'relative p-4 rounded-lg border-2 text-left transition-all min-h-[80px]',
                        'hover:border-primary/50',
                        selections[field.key] === 'new'
                          ? 'border-primary bg-primary/5'
                          : 'border-muted bg-muted/30'
                      )}
                    >
                      {selections[field.key] === 'new' && (
                        <div className="absolute top-3 right-3">
                          <Check className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <p className="text-sm leading-relaxed pr-6">
                        {field.newValue || <span className="text-muted-foreground italic">없음</span>}
                      </p>
                    </button>
                  </div>
                </div>
              ))}

              {/* Tags comparison - Side by Side */}
              {tagsField && tagsField.newTags.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between px-1">
                    <span className="text-xs font-medium text-muted-foreground">태그</span>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-5 text-xs px-1.5"
                        onClick={handleSelectAllTags}
                      >
                        전체 추가
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-5 text-xs px-1.5"
                        onClick={handleDeselectAllTags}
                      >
                        전체 해제
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Current Tags - Left */}
                    <div className="p-3 rounded-lg border-2 border-muted bg-muted/30 min-h-[60px]">
                      <div className="flex flex-wrap gap-1.5">
                        {tagsField.currentTags.length > 0 ? (
                          tagsField.currentTags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-muted-foreground italic">태그 없음</span>
                        )}
                      </div>
                    </div>

                    {/* New Tags to Add - Right */}
                    <div className="p-3 rounded-lg border-2 border-primary/30 bg-primary/5 min-h-[60px]">
                      <div className="flex flex-wrap gap-1.5">
                        {tagsField.newTags.map((tag) => (
                          <label
                            key={tag}
                            className="flex items-center gap-1 cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedNewTags.includes(tag)}
                              onCheckedChange={() => handleTagToggle(tag)}
                              className="w-3.5 h-3.5"
                            />
                            <Badge
                              variant={selectedNewTags.includes(tag) ? 'default' : 'outline'}
                              className="text-xs cursor-pointer"
                            >
                              {tag}
                            </Badge>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Preview of merged tags */}
                  {selectedNewTags.length > 0 && (
                    <div className="px-1 pt-2">
                      <div className="text-xs text-muted-foreground mb-1.5">
                        적용 후 태그 ({tagsField.currentTags.length + selectedNewTags.length}개)
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {[...tagsField.currentTags, ...selectedNewTags].map((tag, idx) => (
                          <Badge
                            key={`${tag}-${idx}`}
                            variant={tagsField.currentTags.includes(tag) ? 'secondary' : 'default'}
                            className="text-xs"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            취소
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={loading || !hasChanges}
          >
            {loading ? '적용 중...' : '선택 항목 적용'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
